import DialogContent from '@mui/material/DialogContent'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import { useAddUpdatePaymentNoteMutation } from 'api/billing/addUpdatePaymentNote'
import { CACHE_TAG_ACCOUNT_PAYMENT_LIST, CACHE_TAG_BILLING_PAYMENT_GROUP_DETAILS, CACHE_TAG_BILLING_PAYMENT_LIST } from 'api/cacheTagTypes'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { HHFormTextField } from 'components/form-fields/v5'
import PciNoteWarning from 'components/notes/common/PciNoteWarning'
import T from 'T'
import { handleError } from 'utils/error'

type PaymentNoteFormValues = { note: string }

type AddUpdatePaymentNoteDialogProps = {
  isOpen?: boolean
  accountId: string
  paymentNumber?: string
  existingNote?: string
  onClose: () => void
}

const AddUpdatePaymentNoteDialog = ({
  isOpen = false,
  accountId,
  paymentNumber = '',
  existingNote = '',
  onClose,
}: AddUpdatePaymentNoteDialogProps) => {
  const dispatch = useDispatch()
  const [addUpdatePaymentNote, { isLoading }] = useAddUpdatePaymentNoteMutation()

  const paymentNoteForm = useForm<PaymentNoteFormValues>({ defaultValues: { note: '' } })
  const {
    control,
    reset,
    setFocus,
    handleSubmit,
    formState: { isDirty },
  } = paymentNoteForm

  const onSubmit = (data: PaymentNoteFormValues) => {
    if (!isDirty) {
      onClose()
      return
    }

    addUpdatePaymentNote({ paymentNumber, note: data.note })
      .unwrap()
      .then(() => {
        toast.success(`Note ${existingNote ? 'updated' : 'added'} successfully`)
        onClose()
        dispatch(
          api.util.invalidateTags([
            { type: CACHE_TAG_ACCOUNT_PAYMENT_LIST, id: accountId },
            CACHE_TAG_BILLING_PAYMENT_LIST,
            CACHE_TAG_BILLING_PAYMENT_GROUP_DETAILS,
          ])
        )
      })
      .catch(handleError)
  }

  useEffect(() => {
    if (isOpen) {
      reset({ note: existingNote })
      setTimeout(() => setFocus('note'), 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <HHBaseDialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <HHDialogTitle title={T.PAYMENT_NOTE} onClose={onClose} />
      <DialogContent>
        <FormProvider {...paymentNoteForm}>
          <HHFormTextField
            fullWidth
            multiline
            control={control}
            deprecatedLabel={false}
            label="Note"
            minRows={4}
            name="note"
            placeholder="Leave a note"
            sx={{ mt: 3, '& .MuiOutlinedInput-root': { height: 'inherit' } }}
          />
        </FormProvider>
        <PciNoteWarning />
      </DialogContent>
      <HHDialogActions>
        <CancelButton onClick={onClose} />
        <SaveButton loading={isLoading} onClick={handleSubmit(onSubmit)} />
      </HHDialogActions>
    </HHBaseDialog>
  )
}

export default AddUpdatePaymentNoteDialog
